// Radha — global command palette (⌘K / Ctrl+K). The 2026 SaaS navigation
// pattern (Linear, Vercel, Notion, Raycast): menus don't scale past ~10 items,
// so search replaces clicking through them. Radha has 18 tabs — this is the
// point where a tab bar alone stops being enough. Additive, not a replacement:
// the visible tab bar still works exactly as before.
import React, { useEffect, useMemo, useRef, useState } from "react";

export interface PaletteItem { id: string; label: string; hint?: string }

function fuzzyScore(query: string, target: string): number {
  const q = query.toLowerCase(), t = target.toLowerCase();
  if (!q) return 1;
  if (t.startsWith(q)) return 100 - t.length;
  if (t.includes(q)) return 50 - t.indexOf(q);
  // subsequence match: every query char appears in order in target
  let ti = 0;
  for (const ch of q) {
    ti = t.indexOf(ch, ti);
    if (ti === -1) return -1;
    ti += 1;
  }
  return 10 - t.length;
}

/** Global ⌘K/Ctrl+K listener. Call once near the app root; toggles the boolean
 *  the palette's `open` prop is bound to. Kept separate from the palette itself
 *  so a header hint button can drive the same state. */
export function useCommandPaletteShortcut(open: boolean, setOpen: (v: boolean | ((p: boolean) => boolean)) => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}

export function CommandPalette({ open, setOpen, items, onSelect }: {
  open: boolean; setOpen: (v: boolean) => void; items: PaletteItem[]; onSelect: (id: string) => void;
}) {
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) { setQ(""); setActive(0); setTimeout(() => inputRef.current?.focus(), 10); }
  }, [open]);

  const results = useMemo(() => {
    return items
      .map((it) => ({ it, score: fuzzyScore(q, it.label) }))
      .filter((r) => r.score > -1)
      .sort((a, b) => b.score - a.score)
      .map((r) => r.it);
  }, [items, q]);

  useEffect(() => setActive(0), [q]);

  const choose = (id: string) => { onSelect(id); setOpen(false); };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (results[active]) choose(results[active].id); }
  };

  if (!open) return null;

  return (
    <div onClick={() => setOpen(false)}
      style={{
        position: "fixed", inset: 0, zIndex: 100, display: "flex", justifyContent: "center",
        alignItems: "flex-start", paddingTop: "12vh",
        background: "rgba(8,9,12,.45)", backdropFilter: "blur(3px)", WebkitBackdropFilter: "blur(3px)",
        animation: "radha-palette-fade .12s ease-out",
      }}>
      <div onClick={(e) => e.stopPropagation()} className="sanctuary-card"
        style={{
          width: 520, maxWidth: "90vw", maxHeight: "60vh", display: "flex", flexDirection: "column",
          borderRadius: 16, boxShadow: "0 24px 60px rgba(0,0,0,.35), 0 2px 8px rgba(0,0,0,.12)",
          overflow: "hidden", animation: "radha-palette-in .14s cubic-bezier(.2,.9,.3,1)",
        }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "13px 16px", borderBottom: "1px solid var(--border)" }}>
          <span style={{ color: "var(--accent-gold)", fontSize: 15 }}>⌘</span>
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKeyDown}
            placeholder="Jump to a module…" aria-label="Search modules"
            style={{ flex: 1, border: 0, outline: "none", background: "none", color: "var(--fg)", fontSize: 15, font: "15px Inter" }} />
          <kbd style={{ fontSize: 11, color: "var(--fg-faint)", border: "1px solid var(--border)", borderRadius: 5, padding: "1px 6px", fontFamily: "'DM Mono', ui-monospace, monospace" }}>esc</kbd>
        </div>
        <div style={{ overflowY: "auto", padding: 6 }}>
          {results.length === 0 && (
            <div style={{ padding: "18px 12px", color: "var(--fg-muted)", fontSize: 13, textAlign: "center" }}>No modules match "{q}"</div>
          )}
          {results.map((it, i) => (
            <div key={it.id} onClick={() => choose(it.id)} onMouseEnter={() => setActive(i)}
              className={i === active ? "sanctuary-row-active" : undefined}
              style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "9px 12px", borderRadius: 8, cursor: "pointer", fontSize: 14,
                background: i === active ? "var(--surface-2)" : "transparent",
                borderLeft: "2px solid transparent",
                color: i === active ? "var(--fg)" : "var(--fg-muted)",
              }}>
              <span>{it.label}</span>
              {it.hint && <span style={{ fontSize: 11, color: "var(--fg-faint)" }}>{it.hint}</span>}
            </div>
          ))}
        </div>
      </div>
      <style>{`
        @keyframes radha-palette-fade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes radha-palette-in { from { opacity: 0; transform: translateY(-6px) scale(.98) } to { opacity: 1; transform: none } }
      `}</style>
    </div>
  );
}

export function PaletteHint({ onOpen }: { onOpen: () => void }) {
  return (
    <button onClick={onOpen} title="Open command palette (⌘K)" className="sanctuary-btn"
      style={{
        display: "flex", alignItems: "center", gap: 5, padding: "6px 10px", fontSize: 12,
        border: "1px solid var(--border)", borderRadius: 9, background: "var(--surface)",
        color: "var(--fg-muted)", cursor: "pointer",
      }}>
      <span>Search</span>
      <kbd style={{ fontFamily: "'DM Mono', ui-monospace, monospace", fontSize: 11, opacity: .8 }}>⌘K</kbd>
    </button>
  );
}
